import { AbstractControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Revisione53State } from '../../../../store/revisione53.reducers';
import { getControl, getFormArray } from '@shared/helpers/form.helper';
import { isNilOrEmpty } from '@shared/helpers/utils.helper';
import { cloneDeep, isEqual } from 'lodash-es';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { MyVerbale } from '../../../../models/interfaces/revisione.interface';
import { Revisione53DatiBusinessSelectors } from '../../../../store/selectors/dati-business.selectors';
import { take } from 'rxjs/operators';
import { withChangeDebounced } from '@shared/operators/with-change-debounced.operator';
import { formPaths } from '@shared/constants/form-paths.const';

const setNestedValueByPath = (obj: unknown, path: string, value: unknown): void => {
    const keys = path.split('.');
    let current = obj as Record<string, unknown>;

    keys.slice(0, -1).forEach((key) => {
        if (!current[key] || typeof current[key] !== 'object') {
            current[key] = {};
        }

        current = current[key] as Record<string, unknown>;
    });

    current[keys[keys.length - 1]] = value;
};

const patchIfChanged = (control: AbstractControl, value: unknown): boolean => {
    if (isEqual(control.value, value)) {
        return false;
    }

    control.patchValue(value, { emitEvent: false });
    return true;
};

const updateArrayPathOnStore = (store: Store<Revisione53State>, arrayPath: string, value: unknown): void => {
    updateVerbaleOnStore(store, (myVerbale) => {
        const clone: MyVerbale = cloneDeep(myVerbale);

        setNestedValueByPath(clone, arrayPath, value);

        return clone;
    });
};

export const bindComuneProvincia = (
    form: FormGroup,
    store: Store<Revisione53State>,
    arrayPath: string,
    comune: { codeKey: string; idKey: string; nameKey: string },
    provincia: { codeKey: string; idKey: string; nameKey: string }
) => {
    const arrayControl = getFormArray(form, arrayPath);

    const persistArrayOnStore = (): void => {
        updateArrayPathOnStore(store, arrayPath, arrayControl.getRawValue());
    };

    arrayControl.valueChanges.pipe(withChangeDebounced(arrayControl)).subscribe((items: unknown[]) => {
        if (!Array.isArray(items)) {
            return;
        }

        let hasSyncChanges = false;

        items.forEach((item, index) => {
            const group = arrayControl.at(index) as FormGroup;

            const codComune = item?.[comune.codeKey] ?? '';
            const codProv = item?.[provincia.codeKey] ?? '';

            const comuneIdControl = getControl(group, comune.idKey);
            const comuneNameControl = getControl(group, comune.nameKey);
            const provinciaIdControl = getControl(group, provincia.idKey);
            const provinciaNameControl = getControl(group, provincia.nameKey);

            hasSyncChanges = patchIfChanged(comuneIdControl, codComune) || hasSyncChanges;
            hasSyncChanges = patchIfChanged(provinciaIdControl, codProv) || hasSyncChanges;

            if (!isNilOrEmpty(codComune)) {
                store
                    .select(Revisione53DatiBusinessSelectors.getComune(codComune))
                    .pipe(take(1))
                    .subscribe((c) => {
                        const nome = c?.Nome_comune;

                        if (!isNilOrEmpty(nome) && patchIfChanged(comuneNameControl, nome)) {
                            persistArrayOnStore();
                        }
                    });
            }

            if (!isNilOrEmpty(codProv)) {
                store
                    .select(Revisione53DatiBusinessSelectors.getProvincia(codProv))
                    .pipe(take(1))
                    .subscribe((p) => {
                        const nome = p?.Nome;

                        if (!isNilOrEmpty(nome) && patchIfChanged(provinciaNameControl, nome)) {
                            persistArrayOnStore();
                        }
                    });
            }
        });

        if (hasSyncChanges) {
            persistArrayOnStore();
        }
    });
};

export const my_AllAmmUnComuneProvinciaCH = (form: FormGroup, store: Store<Revisione53State>) => {
    bindComuneProvincia(
        form,
        store,
        `${formPaths.ammControllo}.my_AllAmminUnico`,
        {
            codeKey: 'my_AllAmmUnComuneCH',
            idKey: 'my_AllAmmUnIdComune',
            nameKey: 'my_AllAmmUnComune',
        },
        {
            codeKey: 'my_AllAmmUnProvinciaCH',
            idKey: 'my_AllAmmUnIdProvincia',
            nameKey: 'my_AllAmmUnProvincia',
        }
    );
};

export const my_AllCollSindComuneProvinciaCH = (form: FormGroup, store: Store<Revisione53State>) => {
    bindComuneProvincia(
        form,
        store,
        `${formPaths.ammControllo}.my_AllCollegioSindacale.my_AllElemCollegioSindacale`,
        {
            codeKey: 'my_AllCollSindComuneCH',
            idKey: 'my_AllCollSindIdComune',
            nameKey: 'my_AllCollSindComune',
        },
        {
            codeKey: 'my_AllCollSindProvinciaCH',
            idKey: 'my_AllCollSindIdProvincia',
            nameKey: 'my_AllCollSindProvincia',
        }
    );
};

export const my_AllConsAmmComuneProvinciaCH = (form: FormGroup, store: Store<Revisione53State>) => {
    bindComuneProvincia(
        form,
        store,
        `${formPaths.ammControllo}.my_AllConsiglioAmministrazione.my_AllElemConsiglioAmministrazione`,
        {
            codeKey: 'my_AllConsAmmComuneCH',
            idKey: 'my_AllConsAmmIdComune',
            nameKey: 'my_AllConsAmmComune',
        },
        {
            codeKey: 'my_AllConsAmmProvinciaCH',
            idKey: 'my_AllConsAmmIdProvincia',
            nameKey: 'my_AllConsAmmProvincia',
        }
    );
};

export const my_AllLiquidatoriComuneCH = (form: FormGroup, store: Store<Revisione53State>) => {
    bindComuneProvincia(
        form,
        store,
        `${formPaths.ammControllo}.my_AllLiquidatori.my_AllElemLiquidatori`,
        {
            codeKey: 'my_AllLiquidatoriComuneCH',
            idKey: 'my_AllLiquidatoriIdComune',
            nameKey: 'my_AllLiquidatoriComune',
        },
        {
            codeKey: 'my_AllLiquidatoriProvinciaCH',
            idKey: 'my_AllLiquidatoriIdProvincia',
            nameKey: 'my_AllLiquidatoriProvincia',
        }
    );
};
